import SubHeader from "../Subheader/Subheader";
import CustomBreadcrumbs from "../Tools/Breadcumbs";
import { useSelector } from "react-redux";
import { useRef, useEffect, useState } from "react";
import moment from "moment";
import { toast } from 'react-toastify';
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import {
    get_ppa_bank_ready,
    updateOfflinePPADebitStatus,
    approveBankReadyPendingRecords,
} from '../../helper/Api';
import "./BankReadyPPA.css";
import Spinner from '../../components/Tools/spinner';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    MenuItem,
    Stack,
    Switch,
} from "@mui/material";
import { styled } from "@mui/material/styles";

const AntSwitch = styled(Switch)(({ theme }) => ({
    width: 28,
    height: 16,
    padding: 0,
    display: "flex",
    "&:active": {
        "& .MuiSwitch-thumb": {
            width: 15,
        },
        "& .MuiSwitch-switchBase.Mui-checked": {
            transform: "translateX(9px)",
        },
    },
    "& .MuiSwitch-switchBase": {
        padding: 2,
        "&.Mui-checked": {
            transform: "translateX(12px)",
            color: "#fff",
            "& + .MuiSwitch-track": {
                opacity: 1,
                backgroundColor: "#1890ff",
                ...theme.applyStyles("dark", {
                    backgroundColor: "#177ddc",
                }),
            },
        },
    },
    "& .MuiSwitch-thumb": {
        boxShadow: "0 2px 4px 0 rgb(0 35 11 / 20%)",
        width: 12,
        height: 12,
        borderRadius: 6,
        transition: theme.transitions.create(["width"], {
            duration: 200,
        }),
    },
    "& .MuiSwitch-track": {
        borderRadius: 16 / 2,
        opacity: 1,
        backgroundColor: "rgba(0,0,0,.25)",
        boxSizing: "border-box",
        ...theme.applyStyles("dark", {
            backgroundColor: "rgba(255,255,255,.35)",
        }),
    },
}));

export default function BankReadyPPA() {
    const { financialYears, authData } = useSelector((state) => state.authData);
    const fyIdRef = useRef(localStorage.getItem('globalDateId'));
    const fyList = financialYears.payload.fy_list;
    const handlePageTrigger = () => {
        fyIdRef.current = localStorage.getItem('globalDateId');
    }
    const dataRef = useRef([]);
    const [loading, setLoading] = useState(false);
    const [selectedInvoiceIds, setSelectedInvoiceIds] = useState(new Set());
    const [actionModalOpen, setActionModalOpen] = useState(false);
    const [l2ApproveConfirmOpen, setL2ApproveConfirmOpen] = useState(false);
    const [actionForm, setActionForm] = useState({
        status: "",
        rejectReason: "",
        remark: "",
        actionDate: moment().format("YYYY-MM-DD"),
    });
    /** L2 only: 'all' | 'pending' — passed to fetch API */
    const [bankReadyView, setBankReadyView] = useState("all");

    const userTypeLc = String(authData?.user?.user_type || "").toLowerCase();
    const pendingParamActive = bankReadyView === "pending";
    /** All view → L1 can select; Pending view → L2 can select */
    const showSelectionCheckboxes = pendingParamActive
        ? userTypeLc === "l2"
        : userTypeLc === "l1";

    const showL1DebitActionButton = userTypeLc === "l1" && !pendingParamActive;
    const showL2PendingApproveButton = userTypeLc === "l2" && pendingParamActive;

    /** Same template on header + every row — fixes column drift vs <table> + global CSS */
    const invoiceGridTemplateColumns = showSelectionCheckboxes
        ? "44px minmax(106px,1.05fr) minmax(80px,0.9fr) minmax(108px,1.65fr) minmax(200px,4fr) minmax(100px,1.5fr) minmax(76px,1fr)"
        : "minmax(106px,1.1fr) minmax(80px,0.95fr) minmax(108px,1.7fr) minmax(200px,4.25fr) minmax(100px,1.55fr) minmax(76px,1fr)";

    const getInvoiceId = (invoice) => invoice?.id ?? invoice?.invoice_id ?? invoice?.invoiceId;

    const toggleInvoice = (invoiceId, checked) => {
        if (invoiceId == null) return;
        setSelectedInvoiceIds((prev) => {
            const next = new Set(prev);
            if (checked) next.add(Number(invoiceId));
            else next.delete(Number(invoiceId));
            return next;
        });
    };

    const togglePpaInvoices = (ppa, checked) => {
        const ids = (ppa?.invoices || [])
            .map(getInvoiceId)
            .filter((id) => id != null)
            .map((id) => Number(id));
        setSelectedInvoiceIds((prev) => {
            const next = new Set(prev);
            if (checked) ids.forEach((id) => next.add(id));
            else ids.forEach((id) => next.delete(id));
            return next;
        });
    };

    // L1 debit flow: flat invoice id list (unchanged for updateOfflinePPADebitStatus)
    const invoicePayload = {
        invoices: Array.from(selectedInvoiceIds),
    };

    /** L2 approve API: [{ ppa_id, invoices: [id, ...] }, ...] */
    const buildInvoicesGroupedByPpa = () => {
        const ppas = dataRef.current || [];
        const grouped = [];
        for (const ppa of ppas) {
            const ppaId = ppa?.id ?? ppa?.ppa_id;
            if (ppaId == null) continue;
            const ids = (ppa?.invoices || [])
                .map(getInvoiceId)
                .filter((id) => id != null)
                .map((id) => Number(id))
                .filter((id) => selectedInvoiceIds.has(id));
            if (ids.length === 0) continue;
            grouped.push({ ppa_id: Number(ppaId), invoices: ids });
        }
        return grouped;
    };

    const selectedCount = selectedInvoiceIds.size;

    const openActionModal = () => {
        if (selectedCount === 0) {
            toast.error("Please select at least one invoice.", {
                position: toast.POSITION.TOP_CENTER,
            });
            return;
        }
        setActionForm({
            status: "",
            rejectReason: "",
            remark: "",
            actionDate: moment().format("YYYY-MM-DD"),
        });
        setActionModalOpen(true);
    };

    const closeActionModal = () => {
        setActionModalOpen(false);
    };

    const openL2ApproveConfirm = () => {
        if (selectedCount === 0) {
            toast.error("Please select at least one record to approve.", {
                position: toast.POSITION.TOP_CENTER,
            });
            return;
        }
        setL2ApproveConfirmOpen(true);
    };

    const closeL2ApproveConfirm = () => {
        setL2ApproveConfirmOpen(false);
    };

    const confirmL2ApprovePending = async () => {
        if (selectedCount === 0) {
            toast.error("Please select at least one record to approve.", {
                position: toast.POSITION.TOP_CENTER,
            });
            return;
        }
        try {
            setLoading(true);
            const invoicesGrouped = buildInvoicesGroupedByPpa();
            if (invoicesGrouped.length === 0) {
                toast.error(
                    "Could not map selected records to PPA. Please refresh the page and try again.",
                    { position: toast.POSITION.TOP_CENTER },
                );
                return;
            }
            const res = await approveBankReadyPendingRecords({
                invoices: invoicesGrouped,
            });
            if (res?.data?.status) {
                toast.success(res.data.message || "Selected records approved successfully.", {
                    position: toast.POSITION.TOP_CENTER,
                });
                setSelectedInvoiceIds(new Set());
                closeL2ApproveConfirm();
                await fetchInvoices();
            } else {
                toast.error(res?.data?.message || "Approval request failed.", {
                    position: toast.POSITION.TOP_CENTER,
                });
            }
        } catch (err) {
            const msg =
                err?.response?.data?.message ||
                err?.response?.data?.error ||
                err?.message ||
                "Something went wrong.";
            toast.error(msg, { position: toast.POSITION.TOP_CENTER });
        } finally {
            setLoading(false);
        }
    };

    const submitInvoiceAction = async () => {
        if (selectedCount === 0) {
            toast.error("Please select at least one invoice.", {
                position: toast.POSITION.TOP_CENTER,
            });
            return;
        }
        if (!actionForm.status) {
            toast.error("Please select status.", { position: toast.POSITION.TOP_CENTER });
            return;
        }
        if (actionForm.status === "rejected" && !actionForm.rejectReason.trim()) {
            toast.error("Please enter reason for rejection.", {
                position: toast.POSITION.TOP_CENTER,
            });
            return;
        }
        if (!actionForm.remark.trim()) {
            toast.error("Please enter additional remark.", {
                position: toast.POSITION.TOP_CENTER,
            });
            return;
        }
        if (!actionForm.actionDate) {
            toast.error("Please select action date.", { position: toast.POSITION.TOP_CENTER });
            return;
        }
        const today = moment().format("YYYY-MM-DD");
        if (actionForm.actionDate > today) {
            toast.error("Action date cannot be in the future.", {
                position: toast.POSITION.TOP_CENTER,
            });
            return;
        }

        try {
            setLoading(true);
            const payload = {
                invoices: invoicePayload.invoices,
                status: actionForm.status, // "successful_debit" | "rejected"
                reject_reason:
                    actionForm.status === "rejected" ? actionForm.rejectReason.trim() : "",
                remark: actionForm.remark.trim(),
                action_date: actionForm.actionDate,
            };
            const res = await updateOfflinePPADebitStatus(payload);
            if (res?.data?.status) {
                toast.success(res.data.message || "Submitted successfully.", {
                    position: toast.POSITION.TOP_CENTER,
                });
                setSelectedInvoiceIds(new Set());
                setActionModalOpen(false);
                await fetchInvoices();
            } else {
                toast.error(res?.data?.message || "Request failed.", {
                    position: toast.POSITION.TOP_CENTER,
                });
                setActionModalOpen(false);
            }
        } catch (err) {
            const msg =
                err?.response?.data?.message ||
                err?.response?.data?.error ||
                err?.message ||
                "Something went wrong.";
            toast.error(msg, { position: toast.POSITION.TOP_CENTER });
            setActionModalOpen(false);
        } finally {
            setLoading(false);
        }
    };

    const fetchInvoices = async () => {
        try {
            setLoading(true);
            const invoices_response = await get_ppa_bank_ready({
                user_type: authData?.user?.user_type?.toUpperCase() || "L1",
                department_id: authData?.user?.department_id || "",
                ...(bankReadyView === "pending" ? { pending: true } : {}),
            });
            if (invoices_response.data.status) {
                const result = invoices_response?.data?.result || [];
                dataRef.current = result;
                setLoading(false);
            }
        } catch (error) {
            setLoading(false);
            toast.error('Something went wrong.', { position: toast.POSITION.TOP_CENTER });
        }
    }
    useEffect(() => {
        fetchInvoices();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bankReadyView]);

    useEffect(() => {
        setSelectedInvoiceIds(new Set());
    }, [bankReadyView]);

  
    return (
        <div>
            {loading && <div > <Spinner/> </div>}
            <SubHeader fy_list={fyList} handlePageTrigger={handlePageTrigger} />
            <div className="row" style={{ padding: "10px 16px" }}>
                <div className="col-md-6">
                    <h4 style={{ color: "#28326f", margin:0}}>Bank Ready PPA</h4>
                    <CustomBreadcrumbs linksArray={[]} title="Bank Ready PPA"/>
                </div>
                <div className="col-md-6">
                    <div className="d-flex gap-2 align-items-center flex-wrap justify-content-end" style={{ width: "auto", float: "right" }}>
                        {String(authData?.user?.user_type || "").toLowerCase() === "l2" && (
                            <div
                                className="d-flex align-items-center gap-2 bg-white px-3 py-2 rounded-3"
                                style={{
                                    border: "1px solid rgba(40, 49, 111, 0.20)",
                                    boxShadow: "0 6px 18px rgba(0,0,0,0.06)",
                                }}
                            >
                                <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                                    <p
                                        className={`m-0 fs-14px fw-500 ${
                                            bankReadyView === "all" ? "text-secondary-100" : "text-black-50"
                                        }`}
                                    >
                                        All
                                    </p>
                                    <AntSwitch
                                        checked={bankReadyView === "pending"}
                                        onChange={(e) =>
                                            setBankReadyView(e.target.checked ? "pending" : "all")
                                        }
                                        sx={{ mx: 0.75 }}
                                        inputProps={{
                                            "aria-label":
                                                "Toggle between all bank-ready PPA rows and pending only",
                                        }}
                                    />
                                    <p
                                        className={`m-0 fs-14px fw-500 ${
                                            bankReadyView === "pending" ? "text-secondary-100" : "text-black-50"
                                        }`}
                                    >
                                        Pending
                                    </p>
                                </Stack>
                            </div>
                        )}
                        {showL1DebitActionButton && (
                            <button
                                type="button"
                                className="submit_button"
                                disabled={selectedCount === 0}
                                onClick={openActionModal}
                            >
                                {selectedCount} records selected
                            </button>
                        )}
                        {showL2PendingApproveButton && (
                            <button
                                type="button"
                                className="submit_button"
                                disabled={selectedCount === 0}
                                onClick={openL2ApproveConfirm}
                            >
                                {selectedCount} records approve
                            </button>
                        )}
                    </div>
                </div>
            </div>
            <Dialog
                open={l2ApproveConfirmOpen}
                onClose={closeL2ApproveConfirm}
                maxWidth="sm"
                fullWidth
                aria-labelledby="bank-ready-l2-approve-title"
            >
                <DialogTitle id="bank-ready-l2-approve-title">Approve selected records</DialogTitle>
                <DialogContent dividers>
                    <p className="mb-0" style={{ fontSize: 15, lineHeight: 1.55, color: "#333" }}>
                        Are you sure you want to approve <strong>{selectedCount}</strong> selected
                        record{selectedCount === 1 ? "" : "s"}? After you confirm, all selected records
                        will move forward to the next stage — where any{" "}
                        <strong>required changes</strong> can be tracked and completed.
                    </p>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={closeL2ApproveConfirm} color="inherit">
                        Cancel
                    </Button>
                    <Button onClick={confirmL2ApprovePending} variant="contained" color="primary">
                        Yes, approve
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={actionModalOpen} onClose={closeActionModal} maxWidth="sm" fullWidth>
                <DialogTitle>Mark selected invoices</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            select
                            label="Status"
                            value={actionForm.status}
                            onChange={(e) =>
                                setActionForm((p) => ({
                                    ...p,
                                    status: e.target.value,
                                    rejectReason: e.target.value === "rejected" ? p.rejectReason : "",
                                }))
                            }
                            fullWidth
                            required
                        >
                            <MenuItem value="success">Successful debit</MenuItem>
                            <MenuItem value="rejected">Rejected</MenuItem>
                        </TextField>

                        {actionForm.status === "rejected" && (
                            <TextField
                                label="Reason for rejection"
                                value={actionForm.rejectReason}
                                onChange={(e) =>
                                    setActionForm((p) => ({ ...p, rejectReason: e.target.value }))
                                }
                                fullWidth
                                multiline
                                minRows={2}
                                required
                            />
                        )}

                        <TextField
                            label="Additional remark"
                            value={actionForm.remark}
                            onChange={(e) =>
                                setActionForm((p) => ({ ...p, remark: e.target.value }))
                            }
                            fullWidth
                            multiline
                            minRows={2}
                            required
                        />

                        <TextField
                            type="date"
                            label="Action date"
                            value={actionForm.actionDate}
                            onChange={(e) =>
                                setActionForm((p) => ({ ...p, actionDate: e.target.value }))
                            }
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ max: moment().format("YYYY-MM-DD") }}
                            fullWidth
                            required
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={closeActionModal} color="inherit">
                        Cancel
                    </Button>
                    <Button onClick={submitInvoiceAction} variant="contained">
                        Submit
                    </Button>
                </DialogActions>
            </Dialog>
            <div className='p-3'>
                {
                (dataRef.current || [])?.length > 0 && (dataRef.current || []).map((ppa, index) => (
                    (() => {
                        const ppaInvoiceIds = (ppa?.invoices || [])
                            .map(getInvoiceId)
                            .filter((id) => id != null)
                            .map((id) => Number(id));
                        const selectedCount = ppaInvoiceIds.filter((id) => selectedInvoiceIds.has(id)).length;
                        const allSelected = ppaInvoiceIds.length > 0 && selectedCount === ppaInvoiceIds.length;
                        const indeterminate = selectedCount > 0 && !allSelected;
                        if(ppa.invoices.length > 0) {
                            return (
                                <div className="border-prime-100 border-1 overflow-hidden rounded-3 mt-2" key={index}>
                                    <FormControlLabel sx={{ backgroundColor: "#DFF0FF", width: "100%", left: "1%" }} className="position-relative"
                                    control={
                                        <>
                                        {showSelectionCheckboxes && (
                                            <Checkbox
                                                checked={allSelected}
                                                indeterminate={indeterminate}
                                                onChange={(e) => togglePpaInvoices(ppa, e.target.checked)}
                                            />
                                        )}
                                        
                                        <div className="d-flex justify-content-between align-items-center w-100 px-1 pe-2">
                                            <div className="d-flex gap-3 align-items-center">
                                            <div className="fs-12px d-flex align-items-center gap-1">
                                                Ppa No: <span className="fs-14px fw-500">{ppa.ppa_no}-{ppa.id}</span>
                                            </div>
                                            <div className="fs-12px d-flex align-items-center gap-1">
                                                Ppa Date:{" "}
                                                <span className="fs-14px fw-500">{moment(ppa.ppa_date, 'YYYY-MM-DD').format('DD-MM-YYYY')}</span>
                                            </div>
                                            </div>
                    
                                            <div className="fs-12px d-flex align-items-center gap-1">
                                            Total Amount: <span className="fs-14px fw-500">{ppa.amount}</span>
                                            </div>
                                        </div>
                                        </>
                                    }
                                    />
                                    <div
                                        className="bank-ready-invoices-shell"
                                        role="table"
                                        aria-label="Invoice lines"
                                    >
                                        <div
                                            className="bank-ready-grid-row bank-ready-grid-row--head"
                                            role="row"
                                            style={{
                                                gridTemplateColumns: invoiceGridTemplateColumns,
                                            }}
                                        >
                                            {showSelectionCheckboxes && (
                                                <div
                                                    role="columnheader"
                                                    className="bank-ready-grid-h bank-ready-grid-h--check"
                                                    aria-label="Select"
                                                />
                                            )}
                                            <div role="columnheader" className="bank-ready-grid-h">
                                                Invoice No
                                            </div>
                                            <div role="columnheader" className="bank-ready-grid-h">
                                                Voucher Date
                                            </div>
                                            <div role="columnheader" className="bank-ready-grid-h">
                                                Voucher No
                                            </div>
                                            <div role="columnheader" className="bank-ready-grid-h">
                                                Beneficiary Name
                                            </div>
                                            <div role="columnheader" className="bank-ready-grid-h">
                                                Sanction Order No
                                            </div>
                                            <div
                                                role="columnheader"
                                                className="bank-ready-grid-h bank-ready-grid-h--amount"
                                            >
                                                Amount
                                            </div>
                                        </div>
                                        {(ppa?.invoices || []).map((invoice, invoiceIndex) => {
                                            const invoiceId = getInvoiceId(invoice);
                                            const checked =
                                                invoiceId != null &&
                                                selectedInvoiceIds.has(Number(invoiceId));
                                            return (
                                                <div
                                                    key={invoiceIndex}
                                                    className={`bank-ready-grid-row bank-ready-grid-row--body${
                                                        invoiceIndex % 2 === 1
                                                            ? " bank-ready-grid-row--stripe"
                                                            : ""
                                                    }`}
                                                    role="row"
                                                    style={{
                                                        gridTemplateColumns:
                                                            invoiceGridTemplateColumns,
                                                    }}
                                                >
                                                    {showSelectionCheckboxes && (
                                                        <div
                                                            role="cell"
                                                            className="bank-ready-grid-cell bank-ready-grid-cell--check"
                                                        >
                                                            <Checkbox
                                                                checked={!!checked}
                                                                onChange={(e) =>
                                                                    toggleInvoice(
                                                                        invoiceId,
                                                                        e.target.checked,
                                                                    )
                                                                }
                                                                size="small"
                                                                sx={{ p: 0 }}
                                                            />
                                                        </div>
                                                    )}
                                                    <div role="cell" className="bank-ready-grid-cell">
                                                        {invoice.invoice_no}
                                                    </div>
                                                    <div
                                                        role="cell"
                                                        className="bank-ready-grid-cell bank-ready-grid-cell--nowrap"
                                                    >
                                                        {moment(
                                                            invoice.voucher_date,
                                                            "YYYY-MM-DD",
                                                        ).format("DD-MM-YYYY")}
                                                    </div>
                                                    <div
                                                        role="cell"
                                                        className="bank-ready-grid-cell"
                                                        title={invoice.voucher_no}
                                                    >
                                                        {invoice.voucher_no}
                                                    </div>
                                                    <div
                                                        role="cell"
                                                        className="bank-ready-grid-cell bank-ready-grid-cell--beneficiary"
                                                        title={invoice.company_name}
                                                    >
                                                        {invoice.company_name}
                                                    </div>
                                                    <div
                                                        role="cell"
                                                        className="bank-ready-grid-cell"
                                                        title={invoice.sanction_order_no}
                                                    >
                                                        {invoice.sanction_order_no}
                                                    </div>
                                                    <div
                                                        role="cell"
                                                        className="bank-ready-grid-cell bank-ready-grid-cell--amount"
                                                    >
                                                        {invoice.payable_amount}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        }
                    })()
                ))
                }
            </div>
        </div>
    )
}